/**
 * Generic function to detect duplicate IDs in an array of items
 * @param items - Array of items to check
 * @param getId - Function to extract ID from each item
 * @returns Array of duplicate IDs found
 */
function detectDuplicateIds<T>(items: T[], getId: (item: T) => string): string[] {
  const ids = items.map(getId)
  return ids.filter((id, index) => ids.indexOf(id) !== index)
}

/**
 * Deduplicate an array of items by keeping only the first occurrence of each ID
 * @param items - Array of items to deduplicate
 * @param getId - Function to extract ID from each item
 * @returns Deduplicated array
 */
function deduplicateItems<T>(items: T[], getId: (item: T) => string): T[] {
  return items.filter((item, index) => {
    const id = getId(item)
    return items.findIndex((i) => getId(i) === id) === index
  })
}

/**
 * Detect duplicate node IDs
 * @param nodes - Array of nodes to check
 * @returns Array of duplicate node IDs
 */
function detectDuplicateNodeIds<T extends { id: string }>(nodes: T[]): string[] {
  return detectDuplicateIds(nodes, (node) => node.id)
}

/**
 * Detect duplicate edge IDs
 * @param edges - Array of edges to check
 * @returns Array of duplicate edge IDs
 */
function detectDuplicateEdgeIds<T extends { id: string }>(edges: T[]): string[] {
  return detectDuplicateIds(edges, (edge) => edge.id)
}

/**
 * Deduplicate nodes by keeping only the first occurrence of each ID
 * @param nodes - Array of nodes to deduplicate
 * @returns Deduplicated array of nodes
 */
function deduplicateNodes<T extends { id: string }>(nodes: T[]): T[] {
  return deduplicateItems(nodes, (node) => node.id)
}

/**
 * Deduplicate edges by keeping only the first occurrence of each ID
 * @param edges - Array of edges to deduplicate
 * @returns Deduplicated array of edges
 */
function deduplicateEdges<T extends { id: string }>(edges: T[]): T[] {
  return deduplicateItems(edges, (edge) => edge.id)
}

/**
 * Validate mindmap data for duplicates
 * @param nodes - Array of nodes to validate
 * @param edges - Array of edges to validate
 * @returns Object with validation results
 */
export function validateMindmapData<TNode extends { id: string }, TEdge extends { id: string }>(
  nodes: TNode[],
  edges: TEdge[]
): {
  hasDuplicates: boolean
  duplicateNodeIds: string[]
  duplicateEdgeIds: string[]
  deduplicatedNodes: TNode[]
  deduplicatedEdges: TEdge[]
} {
  const duplicateNodeIds = detectDuplicateNodeIds(nodes)
  const duplicateEdgeIds = detectDuplicateEdgeIds(edges)
  const hasDuplicates = duplicateNodeIds.length > 0 || duplicateEdgeIds.length > 0

  // Note: Duplicate detection is silent - duplicates are returned for caller to handle

  return {
    hasDuplicates,
    duplicateNodeIds,
    duplicateEdgeIds: duplicateEdgeIds,
    deduplicatedNodes: hasDuplicates ? deduplicateNodes(nodes) : nodes,
    deduplicatedEdges: hasDuplicates ? deduplicateEdges(edges) : edges,
  }
}
